import {
  AlertSpg,
  AsyncCallArgs,
  DateFormField,
  FormSection,
  GridContainer,
  GridItem,
  InputFormField,
  isNotEmpty,
  SelectFormField,
  useAsyncCall,
  useEffectSkipFirstRender,
} from "@duosoftbg/nacid-components";
import React, { useEffect, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { getGradingScalesByCountryCodeAndYear } from "../../../../../../services/gradingScaleCalls";
import { initialSeRecognitionEducation } from "../../../../../../init/seRecognitionInitialValues";
import { useTranslation } from "react-i18next";
import GradingScaleDetailsSection from "./GradingScaleDetailsSection";

interface DiplomaDataFormSectionProps {
  infoMessage?: string;
  profQualifSection?: boolean;
  gradingScaleSection?: boolean;
  gradingScaleWarning?: string;
  isDiplomaNumberRequired?: boolean;
}

const DiplomaDataFormSection = ({
  infoMessage = undefined,
  profQualifSection = false,
  gradingScaleSection = false,
  gradingScaleWarning = undefined,
  isDiplomaNumberRequired = false,
}: DiplomaDataFormSectionProps) => {
  const { t } = useTranslation();
  return (
    <FormSection label={"t.school.diploma"}>
      <GridContainer spacing={3} mt={0}>
        {infoMessage && (
          <GridItem xs={12} sm={12} md={12}>
            <AlertSpg severity={"info"}>{t(infoMessage)}</AlertSpg>
          </GridItem>
        )}
        <GridItem sm={3} md={2}>
          <InputFormField required={isDiplomaNumberRequired} fieldName={"diplomaNumber"} labelCode={"l.number"} />
        </GridItem>
        <GridItem sm={3} md={2}>
          <DateFormField required={false} fieldName={"diplomaDate"} labelCode={"l.diploma.date"} />
        </GridItem>
        <DiplomaYearFormField />

        {gradingScaleSection && (
          <>
            <GradingScalesSelectFormField />
            <GradingScaleDetailsSection gradingScaleWarning={gradingScaleWarning} />
          </>
        )}
        <GridItem xs={12} sm={12} md={12} style={{ marginTop: "20px" }}>
          <AlertSpg severity={"info"}>{t("m.diploma.url.info")}</AlertSpg>
        </GridItem>
        <GridItem sm={6} md={6}>
          <InputFormField required={false} fieldName={"diplomaRegisterURL"} labelCode={"l.diploma.register.url"} />
        </GridItem>
        <GridItem sm={6} md={6}>
          <InputFormField required={false} fieldName={"diplomaApostilleURL"} labelCode={"l.diploma.apostille.url"} />
        </GridItem>
        {profQualifSection && <ProfessionalQualificationFormSection />}
      </GridContainer>
    </FormSection>
  );
};

const DiplomaYearFormField = () => {
  const { setValue, getValues } = useFormContext();
  const diplomaDate = useWatch({ name: "diplomaDate" });

  useEffect(() => {
    const currentYear = new Date().getFullYear();

    const diplomaYearValue = String(getValues("diplomaYear")).trim();
    const isInteger = /^\d+$/.test(diplomaYearValue);
    const isValidYear = isInteger && Number(diplomaYearValue) > 1900 && Number(diplomaYearValue) <= currentYear;

    if (isNotEmpty(diplomaDate) && !isValidYear) {
      const year = parseInt(diplomaDate.split(/[.\-,]/)[2]);

      setValue("diplomaYear", year || "");
    }
  }, [diplomaDate]);

  return (
    <GridItem sm={3} md={2}>
      <InputFormField required={true} fieldName={"diplomaYear"} labelCode={"l.diploma.year"} />
    </GridItem>
  );
};

const ProfessionalQualificationFormSection = () => {
  const { t } = useTranslation();

  return (
    <>
      <GridItem xs={12} sm={12} md={12} style={{ marginTop: "20px" }}>
        <AlertSpg severity={"info"}>{t("m.professional.qualification.info")}</AlertSpg>
      </GridItem>
      <GridItem sm={6} md={6}>
        <InputFormField fieldName={"professionBgTitle"} labelCode={"l.profession.bg.title"} />
      </GridItem>
      <GridItem sm={6} md={6}>
        <InputFormField fieldName={"professionNativeTitle"} labelCode={"l.profession.native.title"} />
      </GridItem>
    </>
  );
};

const GradingScalesSelectFormField = () => {
  const { setValue } = useFormContext();
  const { asyncCall } = useAsyncCall();

  const isForeignGradingScale = useWatch({ name: "isForeignGradingScale" });
  const schoolCountry = useWatch({ name: "schoolCountry.id" });
  const schoolGradingScaleCountry = useWatch({ name: "schoolGradingScaleCountry.id" });

  const year = useWatch({ name: "diplomaYear" });
  const countryCodeText = String(isForeignGradingScale ? schoolGradingScaleCountry : schoolCountry);
  const yearText = String(year);
  const [gradingScales, setGradingScales] = useState([]);

  const reloadGradingScales = () => {
    if (isNotEmpty(countryCodeText) && isNotEmpty(year)) {
      const fetchDocTypes: AsyncCallArgs = {
        withGlobalBackdrop: false,
        promise: getGradingScalesByCountryCodeAndYear(countryCodeText, year),
        processResponseErrors: false,
        onSuccess: (response) => {
          setGradingScales(response.data);
        },
        onError: () => {
          setGradingScales([]);
        },
      };
      asyncCall(fetchDocTypes);
    } else {
      setGradingScales([]);
    }
  };

  useEffect(() => {
    reloadGradingScales();
  }, []);

  useEffectSkipFirstRender(() => {
    setValue("gradingScale", initialSeRecognitionEducation.gradingScale);
    reloadGradingScales();
  }, [asyncCall, countryCodeText, yearText]);

  return (
    <GridItem sm={6} md={6}>
      <SelectFormField
        required={true}
        fieldName={"gradingScale.id"}
        labelCode={"l.gradingScale"}
        addEmptyOption={true}
        selectOptions={gradingScales.map((scale) => {
          return { value: scale.id, text: scale.name };
        })}
      />
    </GridItem>
  );
};
export default DiplomaDataFormSection;
